import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bug, ChevronDown, ChevronUp } from 'lucide-react';

interface MobileDebugProps {
  enabled?: boolean;
}

export default function MobileDebug({ enabled = false }: MobileDebugProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>({});

  useEffect(() => {
    if (!enabled) return;

    const updateDebugInfo = () => {
      setDebugInfo({
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          availWidth: window.screen.availWidth,
          availHeight: window.screen.availHeight
        },
        touch: {
          maxTouchPoints: navigator.maxTouchPoints,
          touchSupport: 'ontouchstart' in window
        },
        memory: (performance as any).memory ? {
          usedJSHeapSize: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024),
          totalJSHeapSize: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024),
          jsHeapSizeLimit: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024)
        } : null,
        connection: (navigator as any).connection ? {
          effectiveType: (navigator as any).connection.effectiveType,
          downlink: (navigator as any).connection.downlink,
          rtt: (navigator as any).connection.rtt
        } : null,
        timestamp: new Date().toLocaleTimeString()
      });
    };

    updateDebugInfo();
    const interval = setInterval(updateDebugInfo, 5000);

    return () => clearInterval(interval);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Card className="w-80 max-h-96 overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Bug className="h-4 w-4" />
              Mobile Debug
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              className="h-6 w-6 p-0"
            >
              {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
            </Button>
          </CardTitle>
        </CardHeader>
        
        {isOpen && (
          <CardContent className="pt-0 text-xs space-y-2 max-h-80 overflow-y-auto">
            <div>
              <strong>Viewport:</strong> {debugInfo.viewport?.width}x{debugInfo.viewport?.height}
            </div>
            
            <div>
              <strong>Screen:</strong> {debugInfo.screen?.width}x{debugInfo.screen?.height}
            </div>
            
            <div>
              <strong>DPR:</strong> {debugInfo.viewport?.devicePixelRatio}
            </div>
            
            <div>
              <strong>Touch:</strong> {debugInfo.touch?.touchSupport ? 'Yes' : 'No'} 
              ({debugInfo.touch?.maxTouchPoints} points)
            </div>
            
            {debugInfo.memory && (
              <div>
                <strong>Memory:</strong> {debugInfo.memory.usedJSHeapSize}MB / {debugInfo.memory.totalJSHeapSize}MB
              </div>
            )}
            
            {debugInfo.connection && (
              <div>
                <strong>Connection:</strong> {debugInfo.connection.effectiveType} 
                ({debugInfo.connection.downlink}Mbps, {debugInfo.connection.rtt}ms)
              </div>
            )}
            
            <div>
              <strong>User Agent:</strong>
              <div className="text-xs text-gray-600 break-all">
                {debugInfo.userAgent}
              </div>
            </div>
            
            <div className="text-xs text-gray-500">
              Last updated: {debugInfo.timestamp}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
} 